import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AdminLayout, API } from './AdminPages';
import { BarChart3, Users, Eye, MessageSquare, Mail, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell
} from 'recharts';

const PAGE_LABELS = { '/': 'Página Inicial', '/orcamento': 'Orçamento', '/trabalhe-conosco': 'Trabalhe Conosco', '/blog': 'Blog', '/privacidade': 'Privacidade' };
const COLORS = ['#00A8E8', '#0055FF', '#5BC0F8', '#3b82f6', '#818cf8', '#a5b4fc'];

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="admin-card p-6 flex items-center gap-4" data-testid={testId}>
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
      <Icon className="text-white" size={22} />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-gray-400 text-sm">{label}</p>
    </div>
  </div>
);

const HBarList = ({ title, items, labelMap, testId }) => {
  const max = Math.max(...items.map(i => i.count), 1);
  return (
    <div className="admin-card p-6" data-testid={testId}>
      <h3 className="text-white font-bold mb-4">{title}</h3>
      {items.length === 0 && <p className="text-gray-500 text-sm">Sem dados no período</p>}
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={item.name}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-300 truncate pr-2">{(labelMap && labelMap[item.name]) || item.name}</span>
              <span className="text-[#00A8E8] font-bold">{item.count}</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(item.count / max) * 100}%`, background: COLORS[i % COLORS.length] }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/summary?days=${days}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendReport = async () => {
    setSending(true);
    setSentMsg('');
    try {
      const res = await axios.post(`${API}/analytics/send-report`);
      setSentMsg(res.data.message || 'Relatório enviado!');
    } catch (e) {
      setSentMsg('Falha ao enviar relatório');
    }
    setSending(false);
    setTimeout(() => setSentMsg(''), 5000);
  };

  const deviceLabels = { desktop: 'Computador', mobile: 'Celular', tablet: 'Tablet' };

  return (
    <AdminLayout>
      <div data-testid="admin-analytics">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Estatísticas do Site</h1>
            <p className="text-gray-400">Visitantes, páginas e origem do tráfego (dados com consentimento LGPD)</p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)} data-testid={`period-${d}`}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${days === d ? 'bg-[#0055FF] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                {d} dias
              </button>
            ))}
            <button onClick={sendReport} disabled={sending} data-testid="send-report-btn"
              className="btn-primary flex items-center gap-2 text-sm px-4 py-2">
              <Mail size={16} /> {sending ? 'Enviando...' : 'Enviar relatório agora'}
            </button>
          </div>
        </div>

        {sentMsg && <div className="mb-6 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg px-4 py-3 text-sm" data-testid="report-sent-msg">{sentMsg}</div>}

        {loading && (
          <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
            <RefreshCw className="animate-spin" size={20} /> Carregando...
          </div>
        )}

        {!loading && data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <StatCard icon={Users} label="Visitantes únicos" value={data.unique_visitors} testId="stat-visitors" />
              <StatCard icon={Eye} label="Visualizações de página" value={data.total_views} testId="stat-views" />
              <StatCard icon={MessageSquare} label="Orçamentos recebidos" value={data.contacts} testId="stat-contacts" />
            </div>

            <div className="admin-card p-6 mb-8" data-testid="daily-chart">
              <h3 className="text-white font-bold mb-4">Visitantes por dia</h3>
              {data.daily.length === 0 ? (
                <p className="text-gray-500 text-sm py-8 text-center">Ainda sem dados. Os acessos aparecem aqui quando visitantes aceitam os cookies.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.daily}>
                    <defs>
                      <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00A8E8" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#00A8E8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12} tickFormatter={d => d.slice(5).split('-').reverse().join('/')} />
                    <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#0a1128', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff' }}
                      labelFormatter={d => d.split('-').reverse().join('/')} />
                    <Area type="monotone" dataKey="visitors" name="Visitantes" stroke="#00A8E8" fill="url(#gVisitors)" strokeWidth={2} />
                    <Area type="monotone" dataKey="views" name="Visualizações" stroke="#0055FF" fill="transparent" strokeWidth={2} strokeDasharray="4 4" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <HBarList title="Páginas mais vistas" items={data.top_pages} labelMap={PAGE_LABELS} testId="top-pages" />
              <HBarList title="Origem dos visitantes" items={data.sources} testId="traffic-sources" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <HBarList title="Dispositivos" items={data.devices} labelMap={deviceLabels} testId="devices" />
              <HBarList title="Navegadores" items={data.browsers} testId="browsers" />
              <div className="admin-card p-6" data-testid="hourly-chart">
                <h3 className="text-white font-bold mb-4">Horários de acesso</h3>
                {data.hourly.length === 0 ? (
                  <p className="text-gray-500 text-sm">Sem dados no período</p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.hourly}>
                      <XAxis dataKey="hour" stroke="#64748b" fontSize={11} tickFormatter={h => `${h}h`} />
                      <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#0a1128', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff' }}
                        labelFormatter={h => `${h}h (Manaus)`} />
                      <Bar dataKey="count" name="Acessos" radius={[4, 4, 0, 0]}>
                        {data.hourly.map((_, i) => <Cell key={i} fill="#00A8E8" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
              <BarChart3 className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-gray-300 text-sm">
                Um <strong className="text-white">relatório diário</strong> com esses números é enviado automaticamente por e-mail às 07h30 (horário de Manaus) para tomaselli@tomasellisom.com.br. Apenas visitantes que clicam em "Aceitar todos" no banner de cookies são contabilizados (LGPD).
              </p>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
